import { Avatar, Box, Icon, Text, Tooltip, useToggle } from '@factorialco/deprecated-design-system'
import {
  ProjectManagementProjectWorker,
  ProjectionKind,
  useProjectEmployeesDataTable,
} from 'generated/resources/datatable.graphql'
import compact from 'lodash/compact'

import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'

import { formatProjectHours } from 'modules/project_management/lib/formatHours'

import { Amount } from '../../Amount'
import AssignedTag from '../../labelTags/AssignedTag'
import ActionButtons from './TableBody/ActionButtons'
import { IProjectWorkersPanelData } from './types'

type ProjectWorkersDataTableProjection = Parameters<typeof useProjectEmployeesDataTable>[0][number]

const ProjectWorkerNameProjection = (): ProjectWorkersDataTableProjection => {
  return {
    kind: ProjectionKind.Custom,
    identifier: 'fullName',
    title: i18n.t('project_management.table.project_employees.headers.name'),
    template: 'fullName',
    colSpan: 3,
    sortBy: 'fullName',
    render: ({ employee_access_avatar, employee_fullName }) => (
      <Box flexDirection="row" alignItems="center" gap="s16">
        <Box width="s40">
          {employee_fullName && (
            <Avatar size="s40" url={buildUrl(employee_access_avatar)} label={employee_fullName} />
          )}
        </Box>
        <Text.Normal weight="medium">{employee_fullName}</Text.Normal>
      </Box>
    ),
  }
}

const ProjectWorkerHoursProjection = (): ProjectWorkersDataTableProjection => ({
  kind: ProjectionKind.Custom,
  identifier: 'hours',
  title: i18n.t('project_management.table.project_employees.headers.hours'),
  template: 'hours',
  width: 200,
  colSpan: 3,
  render: ({ inputedMinutes }) => (
    <Box minWidth="s80">
      <Text.Normal overflowWrap>{formatProjectHours(Number(inputedMinutes))}</Text.Normal>
    </Box>
  ),
})

const LaborCostComponent = ({
  laborCostCents,
  laborCostCurrency,
  inputedMinutes,
}: {
  laborCostCents: ProjectManagementProjectWorker['laborCostCents']
  laborCostCurrency: string
  inputedMinutes: ProjectManagementProjectWorker['inputedMinutes']
}) => {
  const [toggle, setToggle] = useToggle(false)
  const showWarning = inputedMinutes && laborCostCents === 0

  return (
    <Box flexDirection="row" minWidth="s120">
      <Amount amount={laborCostCents} currency={laborCostCurrency} />
      {showWarning ? (
        <Tooltip.Alt
          open={toggle}
          toggle={setToggle}
          attachment="top center"
          label={i18n.t('project_management.table.project_employees.no_labor_cost_warning')}
        >
          <Box width="s12">
            <Icon.Medium color="radicalRed1000" icon="alert-circle" />
          </Box>
        </Tooltip.Alt>
      ) : null}
    </Box>
  )
}

const ProjectWorkerLaborCostProjection = ({
  projectCurrency,
}: {
  projectCurrency: string
}): ProjectWorkersDataTableProjection => ({
  kind: ProjectionKind.Custom,
  identifier: 'laborCost',
  template: '{{ laborCostCents | divided_by: 100}} {{inputedMinutes}}',
  title: i18n.t('project_management.table.project_employees.headers.labor_cost'),
  render: ({ laborCostCents, inputedMinutes }) => {
    return (
      <LaborCostComponent
        laborCostCents={laborCostCents}
        laborCostCurrency={projectCurrency}
        inputedMinutes={inputedMinutes}
      />
    )
  },
})

const ProjectWorkerSpendingsProjection = ({
  projectCurrency,
}: {
  projectCurrency: string
}): ProjectWorkersDataTableProjection => ({
  kind: ProjectionKind.Custom,
  identifier: 'spendings',
  title: i18n.t('project_management.table.project_employees.headers.spendings'),
  template: 'spendings',
  width: 200,
  colSpan: 3,
  render: ({ spendingCostCents }) => {
    return (
      <Box minWidth="s80">
        <Amount amount={spendingCostCents} currency={projectCurrency} />
      </Box>
    )
  },
})

const ProjectWorkerStatusProjection = (): ProjectWorkersDataTableProjection => {
  return {
    kind: ProjectionKind.Custom,
    identifier: 'assigned',
    title: i18n.t('project_management.table.project_employees.headers.status'),
    template: 'assigned',
    colSpan: 3,
    sortBy: 'assigned',
    render: ({ assigned }) => <AssignedTag employeeStatus={assigned} />,
  }
}

const ProjectWorkerActionsProjection = ({
  projectCurrency,
  canAssignSingleEmployee,
  canSeeLaborCosts,
  showProjectWorkersPanel,
}: {
  projectCurrency: string
  canAssignSingleEmployee: boolean
  canSeeLaborCosts: boolean
  showProjectWorkersPanel: (values: IProjectWorkersPanelData) => void
}): ProjectWorkersDataTableProjection => ({
  identifier: 'actions',
  kind: ProjectionKind.Decorative,
  render: ({
    id,
    employee_id,
    assigned,
    laborCostCents,
    inputedMinutes,
    employee_access_avatar,
    employee_fullName,
  }) => (
    <ActionButtons
      id={Number(id)}
      employeeId={Number(employee_id)}
      assigned={assigned}
      imputedMinutes={inputedMinutes}
      laborCostCents={laborCostCents}
      laborCostCurrency={projectCurrency}
      employeeAccessAvatar={employee_access_avatar}
      employeeFullName={employee_fullName}
      canAssignSingleEmployee={canAssignSingleEmployee}
      canSeeLaborCosts={canSeeLaborCosts}
      showProjectWorkersPanel={showProjectWorkersPanel}
    />
  ),
  width: 80,
})

export const projectWorkerProjections = ({
  canAssignSingleEmployee,
  canSeeLaborCosts,
  canSeeSpendings,
  projectCurrency,
  showProjectWorkersPanel,
}: {
  canAssignSingleEmployee: boolean
  canSeeLaborCosts: boolean
  canSeeSpendings: boolean
  projectCurrency: string
  showProjectWorkersPanel: (values: IProjectWorkersPanelData) => void
}) =>
  compact([
    ProjectWorkerNameProjection(),
    ProjectWorkerHoursProjection(),
    canSeeLaborCosts ? ProjectWorkerLaborCostProjection({ projectCurrency }) : null,
    canSeeSpendings ? ProjectWorkerSpendingsProjection({ projectCurrency }) : null,
    ProjectWorkerStatusProjection(),
    ProjectWorkerActionsProjection({
      projectCurrency,
      canAssignSingleEmployee,
      canSeeLaborCosts,
      showProjectWorkersPanel,
    }),
  ])
